import type { GeometryElement } from 'jsxgraph';
import type { ElementDisplayConfig, FeatureElementDef, FeatureType } from '../../../graphpad.model';
import type { CreatedElement, ElementContext, FunctionElement } from '../../element.model';
import { BaseFeatureCreator } from '../feature.model';
import { resolveFeatureStyle, resolveFeatureVisibility, resolveStylePath } from '../feature-style.model';
import { SECANT_DEFAULT_PARAMS, type SecantParams } from './secant.model';

/**
 * Creates a secant line through two points on a function.
 *
 * Subparts:
 * - `startPoint`: The glider at xStart (draggable by default)
 * - `endPoint`: The glider at xEnd (draggable by default)
 * - `secantLine`: The line connecting the two points
 * - `rateTriangle`: Container for rate triangle elements
 *   - `horizontalLine`, `verticalLine`, `cornerPoint`
 */
export class SecantCreator extends BaseFeatureCreator {
  readonly featureType: FeatureType = 'secant';

  create(
    id: string,
    def: FeatureElementDef<'secant'>,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): CreatedElement {
    const { board, featureTheme } = context;
    const params = def.params as SecantParams | undefined;
    if (!params?.of) {
      throw new Error('Secant feature requires params.of to specify the parent function');
    }
    const parentFn: FunctionElement = this.getParentFunction(params.of, context);
    const objects: GeometryElement[] = [];

    const curveElement = parentFn.objects.find((obj) => obj.elType === 'curve');
    if (!curveElement) {
      throw new Error('Secant feature requires parent function to have a curve element');
    }

    const finite = params.finite ?? SECANT_DEFAULT_PARAMS.finite;
    const showRateTriangle = params.showRateTriangle ?? SECANT_DEFAULT_PARAMS.showRateTriangle;

    // Create start glider
    const startGliderElements = this.createGlider(
      id,
      context,
      display,
      ['startPoint'],
      curveElement,
      params.xStart ?? SECANT_DEFAULT_PARAMS.xStart,
      parentFn,
      'xStart',
      'A',
    );
    const startGliderElement = startGliderElements[0];
    objects.push(...startGliderElements);

    // Create end glider
    const endGliderElements = this.createGlider(
      id,
      context,
      display,
      ['endPoint'],
      curveElement,
      params.xEnd ?? SECANT_DEFAULT_PARAMS.xEnd,
      parentFn,
      'xEnd',
      'B',
    );
    const endGliderElement = endGliderElements[0];
    objects.push(...endGliderElements);

    // Typed gliders for getParams/setParams
    const startGlider = startGliderElement as unknown as {
      X: () => number;
      moveTo: (coords: [number, number]) => void;
    };
    const endGlider = endGliderElement as unknown as {
      X: () => number;
      moveTo: (coords: [number, number]) => void;
    };

    // Create secant line - use board directly for infinite line support
    const secantLineStyle = resolveFeatureStyle('line', this.featureType, context, display, ['secantLine']);
    const secantLineVisible = resolveFeatureVisibility(this.featureType, featureTheme, display, ['secantLine']);

    const secantLine = board.create(finite ? 'segment' : 'line', [startGliderElement, endGliderElement], {
      strokeColor: secantLineStyle.color ? context.themeService.getColor(secantLineStyle.color) : undefined,
      strokeWidth: secantLineStyle.strokeWidth ?? 2,
      dash: secantLineStyle.dash ?? 0,
      visible: secantLineVisible,
      straightFirst: !finite,
      straightLast: !finite,
      withLabel: false,
    }) as GeometryElement;
    objects.push(secantLine);

    if (showRateTriangle) {
      this.createRateTriangle(context, display, startGliderElement, endGliderElement, objects);
    }

    return {
      id,
      type: 'secant',
      objects,
      getParams: () => ({
        xStart: startGlider.X(),
        xEnd: endGlider.X(),
      }),
      setParams: (newParams) => {
        if (newParams['xStart'] !== undefined) {
          startGlider.moveTo([newParams['xStart'], parentFn.evaluate(newParams['xStart'])]);
        }
        if (newParams['xEnd'] !== undefined) {
          endGlider.moveTo([newParams['xEnd'], parentFn.evaluate(newParams['xEnd'])]);
        }
        board.update();
      },
      destroy: () => objects.forEach((obj) => board.removeObject(obj)),
    };
  }

  private createGlider(
    elementId: string,
    context: ElementContext,
    display: ElementDisplayConfig | undefined,
    path: string[],
    curveElement: GeometryElement,
    x: number,
    parentFn: FunctionElement,
    paramName: string,
    name: string,
  ): GeometryElement[] {
    const { elementFactory, featureTheme } = context;
    // Label is now part of style via resolveFeatureStyle
    const style = resolveFeatureStyle('point', this.featureType, context, display, path);
    const visible = resolveFeatureVisibility(this.featureType, featureTheme, display, path);
    const fixed = this.resolveFixed(context, display, path);

    const result = elementFactory.glider({
      coords: [x, parentFn.evaluate(x)],
      slideObject: curveElement,
      name,
      style: { ...style, visible, fixed },
      onDrag: (gliderX) => {
        context.onParamChange?.(elementId, { [paramName]: gliderX });
      },
    });

    const objects: GeometryElement[] = [result.glider];
    if (result.label) {
      objects.push(result.label);
    }
    return objects;
  }

  private createRateTriangle(
    context: ElementContext,
    display: ElementDisplayConfig | undefined,
    startGlider: GeometryElement,
    endGlider: GeometryElement,
    objects: GeometryElement[],
  ): void {
    const { elementFactory, featureTheme } = context;
    const rateTriangleVisible = resolveFeatureVisibility(this.featureType, featureTheme, display, ['rateTriangle']);

    const typedStart = startGlider as unknown as { X: () => number; Y: () => number };
    const typedEnd = endGlider as unknown as { X: () => number; Y: () => number };

    // Corner point
    const cornerStyle = resolveFeatureStyle('point', this.featureType, context, display, [
      'rateTriangle',
      'cornerPoint',
    ]);
    const cornerVisible =
      rateTriangleVisible &&
      resolveFeatureVisibility(this.featureType, featureTheme, display, ['rateTriangle', 'cornerPoint']);

    const cornerResult = elementFactory.point({
      coords: [() => typedEnd.X(), () => typedStart.Y()],
      style: { ...cornerStyle, visible: cornerVisible, fixed: true },
    });
    objects.push(cornerResult.point);

    // Horizontal line (Δx) - label is now part of style
    const hLineStyle = resolveFeatureStyle('line', this.featureType, context, display, [
      'rateTriangle',
      'horizontalLine',
    ]);
    const hLineVisible =
      rateTriangleVisible &&
      resolveFeatureVisibility(this.featureType, featureTheme, display, ['rateTriangle', 'horizontalLine']);

    const hLineResult = elementFactory.line({
      a: startGlider,
      b: cornerResult.point,
      name: 'Δx',
      style: { ...hLineStyle, visible: hLineVisible },
    });
    objects.push(hLineResult.line);
    if (hLineResult.lineLabel) {
      objects.push(hLineResult.lineLabel);
    }

    // Vertical line (Δy) - label is now part of style
    const vLineStyle = resolveFeatureStyle('line', this.featureType, context, display, [
      'rateTriangle',
      'verticalLine',
    ]);
    const vLineVisible =
      rateTriangleVisible &&
      resolveFeatureVisibility(this.featureType, featureTheme, display, ['rateTriangle', 'verticalLine']);

    const vLineResult = elementFactory.line({
      a: cornerResult.point,
      b: endGlider,
      name: 'Δy',
      style: { ...vLineStyle, visible: vLineVisible },
    });
    objects.push(vLineResult.line);
    if (vLineResult.lineLabel) {
      objects.push(vLineResult.lineLabel);
    }
  }

  private resolveFixed(
    context: ElementContext,
    display: ElementDisplayConfig | undefined,
    path: string[] = [],
  ): boolean {
    const { featureTheme } = context;
    const featureNode = featureTheme[this.featureType];
    const featureResolved = resolveStylePath(featureNode, path);
    const displayResolved = resolveStylePath(display, path);

    if (displayResolved.point?.fixed !== undefined) {
      return displayResolved.point.fixed;
    }
    if (featureResolved.point?.fixed !== undefined) {
      return featureResolved.point.fixed;
    }
    return false;
  }
}
